import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const idleTimeoutRef = useRef(null);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        if (userData) {
          setUser(JSON.parse(userData));
        } else {
          const me = await api.getCurrentUser();
          localStorage.setItem('user', JSON.stringify(me));
          setUser(me);
        }
      } catch (e) {
        // Token invalid/expired: clear it
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      // Real login via backend API
      const tokenResponse = await api.login({ email, password });
      const accessToken = tokenResponse?.access_token;
      if (!accessToken) {
        return { success: false, error: 'Invalid login response from server' };
      }

      localStorage.setItem('token', accessToken);

      // Fetch current user profile
      const me = await api.getCurrentUser();
      localStorage.setItem('user', JSON.stringify(me));
      setUser(me);

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: 'Login failed' 
      };
    }
  };

  const register = async (name, email, password, role = 'BD') => {
    try {
      // Placeholder: registration flow will be wired to backend admin-only API later
      return {
        success: false,
        error: 'Registration is managed by admin in this version. Please contact an admin user.',
      };
    } catch (error) {
      return { 
        success: false, 
        error: 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Idle session timeout: 10 minutes of inactivity
  useEffect(() => {
    const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

    if (!user) {
      // Clear any existing timers and listeners when logged out
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
      return undefined;
    }

    const resetTimer = () => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }

      idleTimeoutRef.current = setTimeout(() => {
        // Session expired due to inactivity
        alert('Your session has expired due to 10 minutes of inactivity. Please log in again.');
        logout();
        window.location.href = '/login';
      }, IDLE_TIMEOUT_MS);
    };

    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

    activityEvents.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    // Start timer initially
    resetTimer();

    return () => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [user]);

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};